import {Injectable} from '@angular/core';
import {BehaviorSubject, forkJoin, lastValueFrom, Observable, Subject} from 'rxjs';
import {MySitesApiService} from './api/my-sites-api.service';
import {IPendingSite} from '../common/models/interfaces';
import {map, take} from 'rxjs/operators';
import {AccessibilityIssuePayload} from '../types/accessibility-issue.type';
import {SitesManager} from '../common/elf-store/sites/sites.manager';
import {SitesQuery} from '../common/elf-store/sites/sites.query';
import {SiteSearchEntryDto, SiteSearchResultDto, UwV2SitesSearchListParams} from '../../userway/sdk/UserWayApiClient';

@Injectable({
  providedIn: 'root',
})
export class MySitesService {
  hasAtLeastOneSubscribedSite: boolean = false;
  forwardedSiteName: string;
  mySitesList: string[];

  private readonly sitesDataIsReady$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private readonly siteToRemove$: BehaviorSubject<SiteSearchEntryDto> = new BehaviorSubject(null);
  private readonly reloadSiteList$: Subject<boolean> = new Subject();

  constructor(
    private mySitesApiService: MySitesApiService,
    private sitesManager: SitesManager,
    private sitesQuery: SitesQuery,
  ) {}

  get reloadSiteList(): Observable<boolean> {
    return this.reloadSiteList$.asObservable();
  }

  get siteToRemove(): Observable<SiteSearchEntryDto> {
    return this.siteToRemove$.asObservable();
  }

  get isSitesDataFetched(): boolean {
    return this.sitesDataIsReady$.value;
  }

  get isSitesDataFetched$() {
    return this.sitesDataIsReady$;
  }

  set isSitesDataFetched(value: boolean) {
    this.sitesDataIsReady$.next(value);
  }

  emitSiteListReload() {
    this.reloadSiteList$.next(true);
  }

  emitSiteToRemove(site: SiteSearchEntryDto) {
    this.siteToRemove$.next(site);
  }

  fetchAllSitesData(
    activeSitesSearchParams: UwV2SitesSearchListParams,
    deletedSitesSearchParams: UwV2SitesSearchListParams,
    pendingSiteSearchParams?: UwV2SitesSearchListParams,
  ) {
    this.sitesManager.setLoading(true);
    forkJoin({
      active: this.mySitesApiService.mySites(activeSitesSearchParams),
      deleted: this.mySitesApiService.mySites(deletedSitesSearchParams),
      pending: this.mySitesApiService.mySites(pendingSiteSearchParams),
    }).subscribe(({active, deleted, pending}) => {
      this.sitesManager.setActiveSites(active);
      this.sitesManager.setDeletedSites(deleted);
      this.sitesManager.setPendingSites(pending);
      this.sitesManager.setLoading(false);
      this.sitesDataIsReady$.next(true);
    });
  }

  getMySitesList(): void {
    this.mySitesApiService
      .mySites({
        p: 1,
        q: 99999,
      })
      .subscribe((response: SiteSearchResultDto) => {
        this.mySitesList = response.sites?.map(site => site.host);
      });
  }

  getAllFreeWidgetSites(): Promise<string[]> {
    return lastValueFrom(
      this.mySitesApiService
        .mySites({
          p: 1,
          q: 9999,
        })
        .pipe(
          map((sitesData: SiteSearchResultDto) =>
            sitesData.sites.filter(site => !site.remediationId).map(({host}) => host),
          ),
        ),
    );
  }

  loadMyActiveSites(requestParams: UwV2SitesSearchListParams, showLoading: boolean = true): void {
    this.sitesQuery.sitesDataIsLoading$.pipe(take(1)).subscribe(() => {
      this.sitesManager.setLoading(showLoading);
      this.mySitesApiService.mySites(requestParams).subscribe((response: SiteSearchResultDto) => {
        if (requestParams.isPdfRequest) {
          this.sitesManager.setActivePdfSites(response);
        } else {
          this.sitesManager.setActiveSites(response);
        }
        this.sitesManager.setLoading(false);
        this.sitesDataIsReady$.next(true);
      });
    });
  }

  loadMyDeletedSites(requestParams: UwV2SitesSearchListParams, showLoading: boolean = true): void {
    this.sitesQuery.sitesDataIsLoading$.pipe(take(1)).subscribe(() => {
      this.sitesManager.setLoading(showLoading);
      this.mySitesApiService.mySites(requestParams).subscribe((response: SiteSearchResultDto) => {
        this.sitesManager.setDeletedSites(response);
        this.sitesManager.setLoading(false);
        this.sitesDataIsReady$.next(true);
      });
    });
  }

  loadMyPendingSites(requestParams: UwV2SitesSearchListParams, showLoading: boolean = true) {
    this.sitesQuery.sitesDataIsLoading$.pipe(take(1)).subscribe(() => {
      this.sitesManager.setLoading(showLoading);
      this.mySitesApiService.mySites(requestParams).subscribe((response: SiteSearchResultDto) => {
        this.sitesManager.setPendingSites(response);
        this.sitesManager.setLoading(false);
      });
    });
  }

  removePendingSite(data: IPendingSite): Observable<any> {
    return this.mySitesApiService.updatePendingSite(data, 'REMOVED');
  }

  approvePendingSite(data: IPendingSite): Observable<any> {
    return this.mySitesApiService.updatePendingSite(data, 'ACTIVE');
  }

  getAllSitesStats(): Observable<any> {
    return this.mySitesApiService.getAllSitesStats().pipe(
      map((response: {payload: AccessibilityIssuePayload}) => {
        const {metadata, stats} = response.payload;
        return Object.values(metadata).map(item => ({
          ...item,
          count: stats[item.id].total,
          sitesAffected: stats[item.id].sitesAffected,
        }));
      }),
    );
  }

  fetchOneSite(params: UwV2SitesSearchListParams) {
    return this.mySitesApiService.mySites({
      ...params,
      p: 1,
      q: 1,
    });
  }
}
